import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import styles from "./ListView.module.scss";
import { useListView } from "./useListView";
import cx from "classnames";
import { Checkbox, CheckboxProps } from "../Checkbox";
import { CheckboxState } from "../Checkbox/Checkbox";
import { useMergeRefs } from "../../hooks";

type ItemType = {
  label: string;
  disabled?: boolean;
  [key: string]: unknown;
};

/**
 * Props for the ListViewOption component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type ListViewOptionProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onChange"
> & {
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Callback when the option changes */
  onChange?: (e?: ChangeEvent<HTMLInputElement>, state?: CheckboxState) => void;
} & (
    | {
        /** The item object when using items prop */
        item: ItemType;
      }
    | {
        /** The display label for the option */
        label: string;
      }
  );

/**
 * ListViewOption component for individual selectable items within a listview.
 *
 * Features:
 * - Displays selectable options with proper ARIA attributes
 * - Supports disabled state for non-selectable options
 * - Keyboard navigation support
 * - Accessible with screen reader support
 *
 * @example
 * <ListView.Option item={{ label: "Option 1" }} />
 */
export const ListViewOption = forwardRef<HTMLDivElement, ListViewOptionProps>(
  (props, ref) => {
    const {
      className,
      children,
      onChange,
      style,
      disabled: disabledProp,
      ...remainingProps
    } = props;
    const optionRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergeRefs([ref, optionRef]);
    const itemRef = useRef<ItemType>();
    const cellRef = useRef<HTMLDivElement>(null);
    const rowId = useId();
    const [isSelected, setIsSelected] = useState(false);
    const [isIndeterminate, setIsIndeterminate] = useState(false);
    const {
      selectedItems,
      setSelectedItems,
      indeterminateItems,
      controlled,
      onSelectionChange,
    } = useListView();
    const disabled =
      "item" in remainingProps
        ? disabledProp || remainingProps.item.disabled
        : disabledProp;

    useEffect(() => {
      const focusables = optionRef.current?.querySelectorAll(
        "button:not(:disabled):not([aria-disabled='true']), input:not(:disabled):not([aria-disabled='true']), [tabindex='0']:not(:disabled):not([aria-disabled='true'])",
      );
      focusables?.forEach((item, i) => {
        item.setAttribute("tabindex", "-1");
        if (item.hasAttribute("id")) return;
        item.setAttribute("id", `${rowId}-${i}`);
      });
    }, [rowId]);

    useEffect(() => {
      if ("item" in remainingProps) {
        const isPartOfSelectedItems = selectedItems.some((item) => {
          return (item as ItemType).label === remainingProps.item.label;
        });

        const isPartOfIndeterminateItems = indeterminateItems.some((item) => {
          return (item as ItemType).label === remainingProps.item.label;
        });

        setIsSelected(isPartOfSelectedItems);
        setIsIndeterminate(isPartOfIndeterminateItems);
      } else {
        const isPartOfSelectedItems = selectedItems.includes(
          remainingProps.label,
        );
        const isPartOfIndeterminateItems = indeterminateItems.includes(
          remainingProps.label,
        );

        setIsSelected(isPartOfSelectedItems);
        setIsIndeterminate(isPartOfIndeterminateItems);
      }
    }, [controlled, remainingProps, selectedItems, indeterminateItems]);

    const onChangeHandler: CheckboxProps["onChange"] = (e) => {
      onChange?.(e, { checked: isSelected });
    };

    const onClickHandler: CheckboxProps["onClick"] = () => {
      updateSelectedItems();
    };

    const removeItemFromSelectedItems = (itemToRemove: ItemType | string) => {
      return selectedItems.filter((item) => {
        if (typeof itemToRemove === "string") {
          return item !== itemToRemove;
        }
        return (item as ItemType)?.label !== itemToRemove.label;
      });
    };

    const addItemFromSelectedItems = (itemToAdd: ItemType | string) => {
      return [...selectedItems, itemToAdd];
    };

    function updateSelectedItems() {
      const currentItem =
        "item" in remainingProps ? remainingProps.item : remainingProps.label;
      const isPartOfSelectedItems = selectedItems.some((item) => {
        if (typeof currentItem === "string") {
          return item === currentItem;
        }
        return (item as ItemType)?.label === currentItem.label;
      });

      const newSelectedItems = isPartOfSelectedItems
        ? removeItemFromSelectedItems(currentItem)
        : addItemFromSelectedItems(currentItem);

      if (controlled) {
        onSelectionChange?.(newSelectedItems);
      } else {
        setSelectedItems(newSelectedItems);
        setIsSelected(!isPartOfSelectedItems);
      }
    }

    if ("item" in remainingProps) {
      itemRef.current = remainingProps.item;
      const { item, ...rest } = remainingProps;
      return (
        <div
          className={cx([styles["listview-option"]], className)}
          data-anv="listview-option"
          style={style}
          ref={mergedRef}
          aria-disabled={disabled}
          aria-selected={isSelected}
          aria-label={item.label}
          role="row"
          {...rest}
        >
          <div className={styles["cell"]} ref={cellRef} role="gridcell">
            <Checkbox
              disabled={disabled}
              checked={isIndeterminate ? undefined : isSelected}
              indeterminate={isIndeterminate}
              aria-label={item.label}
              onChange={onChangeHandler}
              onClick={onClickHandler}
            />
          </div>
          {children}
        </div>
      );
    }

    return (
      <div
        className={cx([styles["listview-option"]], className)}
        data-anv="listview-option"
        style={style}
        ref={mergedRef}
        aria-disabled={disabled}
        aria-selected={isSelected}
        aria-label={"label" in props ? props.label : "Select"}
        role="row"
        {...remainingProps}
      >
        <div className={styles["cell"]} ref={cellRef} role="gridcell">
          <Checkbox
            disabled={disabled}
            checked={isSelected}
            aria-label={"label" in props ? props.label : "Select"}
            onChange={onChangeHandler}
            onClick={onClickHandler}
          />
        </div>
        {children}
      </div>
    );
  },
);

ListViewOption.displayName = "ListViewOption";
